/* eslint-disable */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { rome as moment } from '../../../../utils/date';
import { useAppDispatch } from '../../../../app/hooks';
import { tryToFetchHistorical } from '../../../../store/admin/journey-reports/journeyReportsSlice';
import { useGetCurrentUserQuery } from '../../../../store/user/userApi';
import HistoricalReportDetailModal from '../modals/HistoricalReportDetailModal';
import TmReportModal from '../modals/TmReportModal';

const PAGE_SIZE = 20;

const CheckIcon = () => (
    <svg className='w-4 h-4 text-green-600' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2.5} d='M5 13l4 4L19 7' />
    </svg>
);

const DashIcon = () => (
    <span className='w-4 text-center text-gray-300 font-bold'>—</span>
);

const REPORT_STATUS_STYLES: Record<string, { bg: string; text: string }> = {
    signed: { bg: 'bg-green-100', text: 'text-green-800' },
    in_review: { bg: 'bg-yellow-100', text: 'text-yellow-800' },
    draft: { bg: 'bg-gray-100', text: 'text-gray-700' },
};

const TRIP_STATUS_STYLES: Record<string, { bg: string; text: string }> = {
    active: { bg: 'bg-blue-100', text: 'text-blue-800' },
    completed: { bg: 'bg-green-100', text: 'text-green-800' },
    cancelled: { bg: 'bg-red-100', text: 'text-red-800' },
    scheduled: { bg: 'bg-purple-100', text: 'text-purple-800' },
    pending: { bg: 'bg-yellow-100', text: 'text-yellow-800' },
};

const handleDownloadFile = async (url: string, filename: string) => {
    try {
        const response = await fetch(url);
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
    } catch {
        window.open(url, '_blank');
    }
};

const JourneyReportsTab: React.FC = () => {
    const dispatch = useAppDispatch();
    const { data: currentUser } = useGetCurrentUserQuery();
    const currentUserId = currentUser?.data?._id || '';
    const scrollRef = useRef<HTMLDivElement>(null);
    const [trips, setTrips] = useState<any[]>([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [detailModal, setDetailModal] = useState<{ show: boolean; tripId: string; tripData?: any }>({ show: false, tripId: '' });
    const [tmReportModal, setTmReportModal] = useState<{ show: boolean; tripId: string }>({ show: false, tripId: '' });

    /* ── Initial load ── */
    useEffect(() => {
        loadData(1, true);
    }, []);

    const loadData = async (p: number, reset = false) => {
        if (reset) {
            setIsLoading(true);
        } else {
            setIsLoadingMore(true);
        }
        try {
            const result = await dispatch(tryToFetchHistorical({ page: p, size: PAGE_SIZE })).unwrap();
            const list = Array.isArray(result) ? result : (result?.data || result?.elements || result?.trips || result?.items || []);
            if (reset) {
                setTrips(list);
            } else {
                setTrips((prev) => [...prev, ...list]);
            }
            setPage(p);
            setHasMore(list.length >= PAGE_SIZE);
        } catch {
            if (reset) setTrips([]);
            setHasMore(false);
        } finally {
            setIsLoading(false);
            setIsLoadingMore(false);
        }
    };

    /* ── Infinite scroll handler ── */
    const handleScroll = useCallback(() => {
        if (!scrollRef.current || !hasMore || isLoadingMore || isLoading) return;
        const el = scrollRef.current;
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - 100) {
            loadData(page + 1);
        }
    }, [hasMore, isLoadingMore, isLoading, page]);

    const openDetail = (tripId: string, tripData?: any) => {
        setDetailModal({ show: true, tripId, tripData });
    };

    const closeDetail = () => {
        setDetailModal({ show: false, tripId: '' });
    };

    const hasHodReport = (trip: any, type: string) => {
        return !!trip?.hodReports?.[type]?.submittedAt;
    };

    const hasTmReport = (trip: any) => {
        return !!trip?.tmReportSubmittedAt;
    };

    return (
        <div className='mt-4 font-inter'>
            <div className='border border-[#E9EAEB] rounded-lg overflow-hidden'>
                {/* Header (sticky) */}
                <div className='grid grid-cols-[1.8fr_1.2fr_0.7fr_0.7fr_0.5fr_0.5fr_0.6fr_0.5fr_0.8fr] bg-[#FAFAFA] border-b border-[#E9EAEB] px-4 py-3'>
                    <span className='text-xs font-semibold text-gray-500 uppercase'>Itinerary</span>
                    <span className='text-xs font-semibold text-gray-500 uppercase text-center'>Dates</span>
                    <span className='text-xs font-semibold text-gray-500 uppercase text-center'>Trip Status</span>
                    <span className='text-xs font-semibold text-gray-500 uppercase text-center'>Report Status</span>
                    <span className='text-xs font-semibold text-gray-500 uppercase text-center'>Cabin</span>
                    <span className='text-xs font-semibold text-gray-500 uppercase text-center'>Bar</span>
                    <span className='text-xs font-semibold text-gray-500 uppercase text-center'>Restaurant</span>
                    <span className='text-xs font-semibold text-gray-500 uppercase text-center'>TM</span>
                    <span className='text-xs font-semibold text-gray-500 uppercase text-center'>Actions</span>
                </div>

                {/* Scrollable rows */}
                <div
                    ref={scrollRef}
                    onScroll={handleScroll}
                    className='overflow-y-auto'
                    style={{ maxHeight: '600px' }}
                >
                    {/* Initial loading */}
                    {isLoading && (
                        <div className='flex items-center justify-center py-12'>
                            <div className='w-5 h-5 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin' />
                        </div>
                    )}

                    {/* Rows */}
                    {!isLoading && trips.map((trip: any, idx: number) => {
                        const tripId = trip?._id || trip?.tripId || '';
                        const itinerary = trip?.itineraryTitle || trip?.itinerary?.journeyTitle || trip?.journeyTitle || '—';
                        const startDate = trip?.startDate ? moment(trip.startDate).format('DD MMM YYYY') : '—';
                        const endDate = trip?.endDate ? moment(trip.endDate).format('DD MMM YYYY') : '';
                        const tripStatus = trip?.status || '—';
                        const reportStatus = trip?.reportStatus || null;
                        const rStyle = reportStatus ? (REPORT_STATUS_STYLES[reportStatus] || REPORT_STATUS_STYLES.draft) : null;
                        const tStyle = tripStatus !== '—' ? (TRIP_STATUS_STYLES[tripStatus.toLowerCase()] || { bg: 'bg-gray-100', text: 'text-gray-700' }) : null;

                        return (
                            <div
                                key={tripId + '-' + idx}
                                className='grid grid-cols-[1.8fr_1.2fr_0.7fr_0.7fr_0.5fr_0.5fr_0.6fr_0.5fr_0.8fr] items-center px-4 py-3 border-b border-[#E9EAEB] hover:bg-gray-50 dark:hover:bg-[#232323] transition'
                            >
                                <span className='text-sm text-gray-800 dark:text-white truncate pr-2'>{itinerary}</span>
                                <span className='text-xs text-gray-600 dark:text-white text-center'>
                                    {startDate}{endDate ? ` – ${endDate}` : ''}
                                </span>
                                <span className='flex justify-center'>
                                    {tStyle ? (
                                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium capitalize ${tStyle.bg} ${tStyle.text}`}>
                                            {tripStatus}
                                        </span>
                                    ) : (
                                        <span className='text-xs text-gray-400'>—</span>
                                    )}
                                </span>
                                <span className='flex justify-center'>
                                    {rStyle ? (
                                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${rStyle.bg} ${rStyle.text}`}>
                                            {reportStatus.replace('_', ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())}
                                        </span>
                                    ) : (
                                        <span className='text-xs text-gray-400'>—</span>
                                    )}
                                </span>
                                <span className='flex justify-center'>
                                    {hasHodReport(trip, 'cabin') ? <CheckIcon /> : <DashIcon />}
                                </span>
                                <span className='flex justify-center'>
                                    {hasHodReport(trip, 'bar') ? <CheckIcon /> : <DashIcon />}
                                </span>
                                <span className='flex justify-center'>
                                    {hasHodReport(trip, 'restaurant') ? <CheckIcon /> : <DashIcon />}
                                </span>
                                <span className='flex justify-center'>
                                    {hasTmReport(trip) ? <CheckIcon /> : <DashIcon />}
                                </span>
                                <span className='flex items-center justify-center gap-2 flex-wrap'>
                                    <button
                                        onClick={() => openDetail(tripId, trip)}
                                        className='text-xs font-medium text-[#2D8EFF] hover:text-[#2578DB] transition dark:text-white'
                                    >
                                        View
                                    </button>
                                    {currentUserId && trip?.trainManagerId === currentUserId && trip?.reportId && (
                                        <button
                                            onClick={() => setTmReportModal({ show: true, tripId })}
                                            className='text-xs font-medium text-orange-600 hover:text-orange-800 transition'
                                        >
                                            {reportStatus === 'signed' ? 'Edit' : 'Sign Off'}
                                        </button>
                                    )}
                                    {trip?.docxUrl && (
                                        <button
                                            onClick={() => handleDownloadFile(trip.docxUrl, `report-${tripId}.docx`)}
                                            className='hover:opacity-70 transition'
                                            title='Download DOCX'
                                        >
                                            <img src='/svg/file-docx-color-green-icon.svg' alt='DOCX' className='w-5 h-5' />
                                        </button>
                                    )}
                                    {trip?.pdfUrl && (
                                        <button
                                            onClick={() => handleDownloadFile(trip.pdfUrl, `report-${tripId}.pdf`)}
                                            className='hover:opacity-70 transition'
                                            title='Download PDF'
                                        >
                                            <img src='/svg/download-pdf-icon.svg' alt='PDF' className='w-5 h-5 dark:contrast-0' />
                                        </button>
                                    )}
                                </span>
                            </div>
                        );
                    })}

                    {/* Loading more spinner */}
                    {isLoadingMore && (
                        <div className='flex items-center justify-center py-4'>
                            <div className='w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin' />
                        </div>
                    )}

                    {/* Empty */}
                    {!isLoading && trips.length === 0 && (
                        <div className='text-center py-12 text-gray-400 text-sm'>
                            No journey reports found.
                        </div>
                    )}
                </div>
            </div>

            {/* Detail modal */}
            <HistoricalReportDetailModal
                open={detailModal.show}
                tripId={detailModal.tripId}
                tripData={detailModal.tripData}
                onClose={closeDetail}
            />

            {/* TM Report modal (for train managers to sign off) */}
            <TmReportModal
                open={tmReportModal.show}
                tripId={tmReportModal.tripId}
                onClose={() => setTmReportModal({ show: false, tripId: '' })}
                onSaved={() => loadData(1, true)}
            />
        </div>
    );
};

export default JourneyReportsTab;
